<template>
    <div class="text-center" v-if="generating">
        <i class="icon-spinner6 spinner mr-2"></i>{{ $t('aiGeneration.loading.waitingForResponse') }}<span class="dots">.</span>
    </div>
    <template v-else>
        <div class="d-flex justify-content-end mb-2">
            <AppButton color="indigo" @click="generateName" :disabled="regenerating || (tokenBalance !== null && tokenBalance <= 0)">
                <i :class="['icon-sync mr-2', { 'spinner': regenerating }]"></i>
                {{ $t('aiGeneration.buttons.regenerate') }}
            </AppButton>
        </div>
        <div class="row">
            <div class="col-lg-6">
                <FormGroup :label="`${$t('labels.name')} (${ $t('aiGeneration.review.old') })`">
                    <Input :model-value="product.names[mainIntegrationLanguage]" readonly/>
                </FormGroup>
            </div>
            <div class="col-lg-6">
                <FormGroup :label="`${$t('labels.name')} (${ $t('aiGeneration.review.new') })`" >
                    <Input :model-value="generatedName" @update:model-value="$emit('update:generated-name', $event)"/>
                </FormGroup>
            </div>
        </div>
    </template>
</template>
<script>
import FormGroup from '../../../../../components/new/form/group/FormGroup.vue';
import Input from '../../../../../components/new/form/Input.vue';
import AppButton from '../../../../../components/new/button/AppButton.vue';

import GeminiService from '../../../../../services/GeminiService/GeminiService';
import MessageNotificator from '../../../../../services/MessageNotificator';
import { TooManyRequestsError } from '../../../../../services/Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../../../../../services/Errors/ServiceUnavailableError';

import { httpClient } from '../../../../../services/HttpClient';

export default {
    name: 'NameScreen',
    components: {
        FormGroup, Input, AppButton
    },
    props: {
        product: {
            type: Object,
            required: true
        },
        geminiService: {
            type: GeminiService,
            required: true
        },
        mainIntegrationLanguage: {
            type: String,
            default: 'en'
        },
        generatedName: {
            type: String,
            default: ''
        },
        integrationService: {
            type: String,
            default: null
        },
        formattedFilters: {
            type: Array,
            default: () => []
        },
        tokenBalance: {
            type: [Number, null],
            default: null
        }
    },
    emits: ['generatingStarted', 'generatingStopped', 'update:generated-name'],
    data() {
        return {
            generating: true,
            regenerating: false,
            prompts: {}
        }
    },
    methods: {
        async generateName() {
            this.regenerating = true;
            this.$emit('generatingStarted')
            const prompt = this.createPrompt();
            
            try {
                const response = await this.geminiService.generateString(prompt, {
                    temperature: 0.2,
                    topP: 0.95,
                    maxOutputTokens: 500
                });
                this.$emit('update:generated-name', response);
            } catch (error) {
                if (error instanceof TooManyRequestsError) {
                    new MessageNotificator().warning(this.$t('alerts.too_many_requests'));
                } else if (error instanceof ServiceUnavailableError) {
                    new MessageNotificator().error(this.$t('alerts.service_unavailable'));
                } else {
                    new MessageNotificator().error(this.$t('alerts.contact_support'));
                }
                return;
            } finally {
                await this.geminiService.flushTokens(this.$route.params.projectUuid, `Генерація назви для товару ${this.product.names[this.mainIntegrationLanguage] || Object.values(this.product.names)[0] || ''}`);
                this.$emit('tokensFlushed');
            }
            this.regenerating = false;
            this.generating = false;
            this.$emit('generatingStopped');
        },
        createPrompt() {
            let prompt = this.prompts[this.integrationService] || this.prompts.default;

            let parametersForGeneration = `
                Filters: ${JSON.stringify(this.formattedFilters)}
                Manufacturer: ${JSON.stringify(this.product.manufacturer || 'Not specified')}
                Current name: ${JSON.stringify(this.product.names[this.mainIntegrationLanguage] || 'Not specified')}
                SKU: ${JSON.stringify(this.product.sku || 'Not specified')}

                Translation language: ${this.$t(`language.${this.mainIntegrationLanguage}`)}

                Return only the product name without any additional text or explanations.
            `;

            return prompt + parametersForGeneration;
        },
        async fetchPrompts() {
            const response = await httpClient.post(`/proxy/http-request`, {
                method: "GET",
                url: "https://fasunok.s3.eu-west-2.amazonaws.com/system/gemini-prompts/name.json"
            });

            if (response.status === 200) {
                this.prompts = response.data;
            }
        }
    },
    async mounted() {
        await this.fetchPrompts();
        await this.generateName();
    }
}
</script>   